import React, { useState, useEffect } from 'react';
import { CreditCard, AlertCircle, Loader, DollarSign, Camera } from 'lucide-react';
import type { CreateRedemptionBodyType, CreateRedemptionResponseType, CheckCouponCodeResponseType } from '@zlinkx/shared-v2';
import { Numeric } from '@/utils/numeric';
import { useTranslation } from '@/i18n';
import { formatApiErrorMessage } from '@/utils/error-formatters';
import QRScanner from './QRScanner';
import CouponPreviewCard from './CouponPreviewCard';
import DiscountCalculator from './DiscountCalculator';
import { toast } from 'react-hot-toast';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface RedemptionFormProps {
	onRedeem: (data: CreateRedemptionBodyType) => Promise<CreateRedemptionResponseType>;
	isLoading: boolean;
	cashierCode: string;
	/** Pre-validated coupon from CouponScanEntry (Story 7.3). When provided, skips QR scanning. */
	validatedCoupon?: ValidatedCoupon;
	/** Story 7.8: Callback when redemption succeeds - passes result and form context to parent */
	onRedeemSuccess?: (result: CreateRedemptionResponseType, formContext: { invoiceId: string; note: string }) => void;
}

const RedemptionForm: React.FC<RedemptionFormProps> = ({
	onRedeem,
	isLoading,
	cashierCode,
	validatedCoupon,
	onRedeemSuccess,
}) => {
	const { t } = useTranslation();
	// Skip scanner if we have a pre-validated coupon from Story 7.3
	const [showScanner, setShowScanner] = useState(!validatedCoupon);
	const [formData, setFormData] = useState<CreateRedemptionBodyType>({
		cashier_code: cashierCode,
		coupon_code: validatedCoupon ? String(validatedCoupon.userCouponId) : '',
		purchase_amount: 0,
		invoice_id: '',
		note: '',
		apply_credit: false,
	});
	// Store bill amount as string for better UX
	const [billAmountInput, setBillAmountInput] = useState<string>('');
	// Real-time purchase amount in cents for discount calculation
	const [purchaseAmountCents, setPurchaseAmountCents] = useState<number>(0);
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Calculate minimum purchase validation (Story 7.4 AC4)
	// minPurchase is in DOLLARS, convert to cents
	const minPurchaseCents = validatedCoupon ? (validatedCoupon.minPurchase ?? 0) * 100 : 0;
	const isBelowMinimum = minPurchaseCents > 0 && purchaseAmountCents < minPurchaseCents;
	const canSubmit = !isLoading && !isBelowMinimum && purchaseAmountCents > 0;

	// Open scanner on mount if no coupon ID is present (skip if we have a validated coupon)
	useEffect(() => {
		if (!formData.coupon_code && !validatedCoupon) {
			setShowScanner(true);
		}
	}, [formData.coupon_code, validatedCoupon]);

	useEffect(() => {
		setFormData(prev => ({
			...prev,
			cashier_code: cashierCode,
		}));
	}, [cashierCode]);

	const handleQRScanSuccess = (couponId: string) => {
		console.log('🎯 [RedemptionForm] QR scan successful, populating coupon ID:', couponId);
		setFormData(prev => ({ ...prev, coupon_code: couponId }));
		setShowScanner(false);
		// Auto-focus on invoice ID field
		setTimeout(() => {
			const invoiceInput = document.getElementById('invoiceId');
			invoiceInput?.focus();
			console.log('⌨️ [RedemptionForm] Auto-focused on invoice ID field');
		}, 100);
	};

	const handleQRScanError = (error: string) => {
		toast.error(error);
		setShowScanner(false);
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.coupon_code.trim()) {
			newErrors.coupon_code = t('merchant.redemption.processRedemption.couponIdentifierError');
		}

		if (!formData.invoice_id || !formData.invoice_id.trim()) {
			newErrors.invoice_id = t('merchant.redemption.processRedemption.invoiceIdRequired');
		} else if (!/^[A-Za-z0-9-_]+$/.test(formData.invoice_id)) {
			newErrors.invoice_id = t('merchant.redemption.processRedemption.invoiceIdPattern');
		}

		// Validate bill amount from input string using Numeric utility
		const billAmountCents = Numeric.parseMoney(billAmountInput);
		if (!billAmountInput.trim() || billAmountCents <= 0) {
			newErrors.billAmount = t('merchant.redemption.processRedemption.billAmountError');
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) return;

		// Story 7.8: Capture form context BEFORE the API call
		const capturedFormContext = {
			invoiceId: formData.invoice_id || '',
			note: formData.note || '',
		};

		try {
			// Convert bill amount to cents before submitting
			const billAmountInCents = Numeric.parseMoney(billAmountInput);
			const submitData: CreateRedemptionBodyType = {
				...formData,
				purchase_amount: billAmountInCents,
				invoice_id: formData.invoice_id.trim(),
				note: (formData.note ?? '').trim(),
			};

			const result = await onRedeem(submitData);

			// Story 7.8: Notify parent of success with result and form context
			onRedeemSuccess?.(result, capturedFormContext);

			// Reset form
			setFormData({
				cashier_code: cashierCode,
				coupon_code: '',
				purchase_amount: 0,
				invoice_id: '',
				note: '',
				apply_credit: false,
			});
			setBillAmountInput('');
			setErrors({});
		} catch (error) {
			console.error('Redemption failed:', error);

			// Format and display error message to user
			const errorMessage = formatApiErrorMessage(error, t);
			toast.error(errorMessage);
		}
	};

	const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = e => {
		void handleSubmit(e);
	};

	const handleInputChange = (
		field: keyof CreateRedemptionBodyType,
		value: string | number | boolean | undefined,
	) => {
		setFormData(prev => ({ ...prev, [field]: value }));

		// Clear error when user starts typing
		if (errors[field as string]) {
			setErrors(prev => ({ ...prev, [field as string]: '' }));
		}
	};

	// If scanner is active and no coupon scanned yet, show scanner
	// Skip scanner if we have a pre-validated coupon from CouponScanEntry
	if (showScanner && !formData.coupon_code && !validatedCoupon) {
		return (
			<QRScanner
				onScanSuccess={handleQRScanSuccess}
				onScanError={handleQRScanError}
				onClose={() => setShowScanner(false)}
			/>
		);
	}

	return (
		<div className='bg-white rounded-xl shadow-sm border border-gray-200'>
			<div className='p-6'>
				<div className='flex items-center mb-6'>
					<CreditCard className='w-6 h-6 text-blue-500 mr-3' />
					<h2 className='text-xl font-semibold text-gray-900'>
						{t('merchant.redemption.processRedemption.title')}
					</h2>
				</div>

				{/* QR Scanner Toggle Button - only show if no validated coupon from Story 7.3 */}
				{!formData.coupon_code && !validatedCoupon && (
					<button
						type='button'
						onClick={() => setShowScanner(true)}
						className='w-full mb-6 flex items-center justify-center px-4 py-3 rounded-lg border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-all'
					>
						<Camera className='w-5 h-5 mr-2' />
						<span className='font-medium'>{t('merchant.redemption.mode.qr')}</span>
					</button>
				)}

				<form onSubmit={handleFormSubmit} className='space-y-6'>
					{/* Coupon Preview Card - Story 7.4 AC1 */}
					{validatedCoupon && (
						<CouponPreviewCard coupon={validatedCoupon} />
					)}

					{/* Coupon Identifier - hidden when pre-validated coupon exists */}
					{!validatedCoupon && (
						<div>
							<label className='block text-sm font-medium text-gray-700 mb-2'>
								{t('merchant.redemption.processRedemption.couponIdentifier')}
							</label>
							<input
								type='text'
								id='couponIdentifier'
								value={formData.coupon_code}
								onChange={e => handleInputChange('coupon_code', e.target.value)}
								placeholder={t('merchant.redemption.processRedemption.couponIdentifierPlaceholder')}
								className={`
                  w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${errors.coupon_code ? 'border-red-300' : 'border-gray-300'}
                `}
							/>
							{errors.coupon_code && (
								<p className='mt-1 text-sm text-red-600 flex items-center'>
									<AlertCircle className='w-4 h-4 mr-1' />
									{errors.coupon_code}
								</p>
							)}
							<p className='mt-1 text-sm text-gray-500'>
								{t('merchant.redemption.processRedemption.couponIdentifierHelp')}
							</p>
						</div>
					)}

					{/* Invoice ID */}
					<div>
						<label className='block text-sm font-medium text-gray-700 mb-2'>
							{t('merchant.redemption.processRedemption.invoiceId')}
						</label>
						<input
							type='text'
							id='invoiceId'
							value={formData.invoice_id || ''}
							onChange={e => handleInputChange('invoice_id', e.target.value)}
							placeholder={t('merchant.redemption.processRedemption.invoiceIdPlaceholder')}
							className={`
								w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500
								${errors.invoice_id ? 'border-red-300' : 'border-gray-300'}
							`}
						/>
						{errors.invoice_id && (
							<p className='mt-1 text-sm text-red-600 flex items-center'>
								<AlertCircle className='w-4 h-4 mr-1' />
								{errors.invoice_id}
							</p>
						)}
					</div>

					{/* Bill Amount */}
					<div>
						<label className='block text-sm font-medium text-gray-700 mb-2'>
							{t('merchant.redemption.processRedemption.billAmount')}
						</label>
						<div className='relative'>
							<DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400' />
							<input
								type='text'
								inputMode='decimal'
								value={billAmountInput}
								onChange={e => {
									const value = e.target.value;
									// Allow digits, one decimal point, and basic decimal formatting
									if (/^\d*\.?\d*$/.test(value)) {
										setBillAmountInput(value);
										// Update cents for real-time calculation (Story 7.4)
										const cents = Numeric.parseMoney(value);
										setPurchaseAmountCents(cents);
										// Clear error when user starts typing
										if (errors.billAmount) {
											setErrors(prev => ({ ...prev, billAmount: '' }));
										}
									}
								}}
								placeholder={t('merchant.redemption.processRedemption.billAmountPlaceholder')}
								className={`
                    w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    ${errors.billAmount ? 'border-red-300' : 'border-gray-300'}
                  `}
							/>
						</div>
						{errors.billAmount && (
							<p className='mt-1 text-sm text-red-600 flex items-center'>
								<AlertCircle className='w-4 h-4 mr-1' />
								{errors.billAmount}
							</p>
						)}
						<p className='mt-1 text-sm text-gray-500'>
							{t('merchant.redemption.processRedemption.billAmountHelp')}
						</p>
					</div>

					{/* Discount Calculator - Story 7.4 AC3 & AC4 */}
					{validatedCoupon && (
						<DiscountCalculator
							coupon={validatedCoupon}
							purchaseAmountCents={purchaseAmountCents}
						/>
					)}

					{/* Redemption Notes */}
					<div>
						<label className='block text-sm font-medium text-gray-700 mb-2'>
							{t('merchant.redemption.processRedemption.notes')}
						</label>
						<textarea
							value={formData.note || ''}
							onChange={e => handleInputChange('note', e.target.value)}
							placeholder={t('merchant.redemption.processRedemption.notesPlaceholder')}
							rows={3}
							className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
						/>
					</div>

					{/* Submit Button - Story 7.4 AC4: Disabled when below minimum or no amount */}
					<button
						type='submit'
						disabled={validatedCoupon ? !canSubmit : isLoading}
						className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center'
					>
						{isLoading ? (
							<>
								<Loader className='w-5 h-5 mr-2 animate-spin' />
								{t('merchant.redemption.processRedemption.processing')}
							</>
						) : (
							<>
								<CreditCard className='w-5 h-5 mr-2' />
								{t('merchant.redemption.processRedemption.submitButton')}
							</>
						)}
					</button>
				</form>
			</div>
		</div>
	);
};

export default RedemptionForm;
